import { createEffect } from "solid-js";
import type { SuggestedQuestion } from "../../types/suggestquestions";
import type { PriorityTag } from "../../types/caseState";

type WorkflowStatus = "draft" | "in_progress" | "completed";

type SessionDto = {
  id: string;
  title: string;
  status: WorkflowStatus;
  contactType: string;
  respondentName?: string;
  interviewerName?: string;
  caseId?: string | null;
  metadata?: any;
  startTime?: string;
  endTime?: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  case_number?: string;
};

/**
 * Loads an existing session and hydrates local UI state.
 * This keeps the page component lean while preserving existing behavior.
 */
export function useSessionHydration(input: {
  sessionId: () => string | null;
  isNewSession: () => boolean;

  // API calls
  getSession: (id: string) => Promise<SessionDto>;
  getCaseById: (
    id: string,
  ) => Promise<{ case_number?: string; metadata?: any }>;
  getSessionUtterances: (id: string) => Promise<{ utterances: any[] }>;
  getSessionInsights: (id: string) => Promise<{ insights: any[] }>;

  // state setters
  setIsReadOnlyMode: (v: boolean) => void;
  setSessionStatus: (v: WorkflowStatus) => void;
  setSessionTitle: (v: string) => void;
  setContactType: (v: string) => void;
  setRespondent: (v: string) => void;
  setCaseNumber: (v: string) => void;
  setAutoLocation: (v: string) => void;
  setStartTime: (v: Date) => void;
  setEndTime: (v: Date) => void;
  setCaseState: (fn: (prev: any) => any) => void;
  ingestBatch: (uts: any[], fullText: string) => void;

  // suggestions/insights hooks
  loadSavedInsights: (data: any) => void;
  replaceSuggestions: (next: SuggestedQuestion[]) => void;
  setEarlierFromPersisted: (next: SuggestedQuestion[]) => void;

  // language settings
  setSourceLang: (v: string) => void;
  setTargetLangUi: (v: string) => void;
  setTargetLangHook: (v: string) => void;
  setInterpreterModeFlag: (v: boolean) => void;
  setCaptureMode: (v: "mic" | "multi" | "file") => void;
  setSpeaker2Lang: (v: string) => void;
  setMetadata: (v: any) => void;
  onHydrated: () => void;
}) {
  createEffect(() => {
    const sessionId = input.sessionId();
    if (!sessionId) {
      input.setIsReadOnlyMode(false);
      input.setSessionStatus("draft");
      // input.onHydrated();
      return;
    }

    if (input.isNewSession()) {
      input.setIsReadOnlyMode(false);
      input.setSessionStatus("draft");
      // input.onHydrated();
      return;
    }

    input
      .getSession(sessionId)
      .then((sessionData) => {
        const isReadOnly = sessionData.status === "completed";
        input.setIsReadOnlyMode(isReadOnly);
        input.setSessionStatus(
          (sessionData.status as any) || (isReadOnly ? "completed" : "draft"),
        );
        input.setSessionTitle(sessionData.title);
        input.setContactType(sessionData.contactType || "");

        if (sessionData.respondentName) {
          input.setRespondent(sessionData.respondentName);
          // Seed reporterName from respondent for slot-seeding and report generation
          input.setCaseState((current) => ({
            ...current,
            reporterName: current.reporterName || sessionData.respondentName,
          }));
        }

        // Restore interpreter + capture mode flags from metadata
        const meta: any = sessionData.metadata || {};
        const isInterp = Boolean(meta.interpreterMode);
        input.setInterpreterModeFlag(isInterp);
        const cm = (meta.captureMode as "mic" | "multi" | "file") ?? "mic";
        input.setCaptureMode(cm === "mic" || cm === "multi" || cm === "file" ? cm : "mic");
        if (cm === "file") {
          (window as any)._captureMode = "file";
        }
        input.setSpeaker2Lang(
          meta.speaker2Lang || sessionData.targetLanguage || "",
        );

        input.setMetadata({
          contact_type: sessionData.contactType,
          respondent_name: sessionData.respondentName,
          source_language: sessionData.sourceLanguage,
          target_language: sessionData.targetLanguage,
          case_number: sessionData.case_number || "",
          interviewer_name: sessionData.interviewerName,
        });

        // Restore timing
        if (sessionData.startTime)
          input.setStartTime(new Date(sessionData.startTime));
        if (sessionData.endTime)
          input.setEndTime(new Date(sessionData.endTime));

        // Restore interview questions from metadata
        const interviewQuestions = meta?.interviewQuestions;
        if (interviewQuestions) {
          input.setCaseState((current) => ({
            ...current,
            askedQuestions: interviewQuestions.asked || [],
            answeredQuestions: (interviewQuestions.answered || []).map(
              (qa: any) => ({
                question: qa.question,
                answer: qa.answer,
                answer_en: qa.answer_en,
                paraphrase: qa.paraphrase,
                quote: qa.quote,
                priority_level: qa.priority_level,
                source: qa.source,
                origin: qa.origin,
                confidence: qa.confidence,
                askedAt: qa.askedAt,
                answeredAt: qa.answeredAt,
                answerStatus: qa.answerStatus,
                fieldKey: qa.fieldKey,
                method: qa.method,
              }),
            ),
            dismissedQuestions: interviewQuestions.dismissed || [],
          }));
        }

        // Restore suggestions + earlier, filtered against asked/answered/dismissed
        const savedSuggested =
          sessionData.metadata?.interviewQuestions?.suggested;
        const savedEarlier = sessionData.metadata?.interviewQuestions?.earlier;

        const askedSet = new Set(
          (sessionData.metadata?.interviewQuestions?.asked || []).map(
            (q: string) => (q || "").trim().toLowerCase(),
          ),
        );
        const answeredSet = new Set(
          (sessionData.metadata?.interviewQuestions?.answered || []).map(
            (qa: any) => (qa.question || "").trim().toLowerCase(),
          ),
        );
        const dismissedSet = new Set(
          (sessionData.metadata?.interviewQuestions?.dismissed || []).map(
            (d: any) =>
              (typeof d === "string" ? d : d.question || "")
                .trim()
                .toLowerCase(),
          ),
        );

        const toSQ = (s: any): SuggestedQuestion =>
          typeof s === "string"
            ? {
                id: s,
                question: s,
                priority_level: "Informational" as PriorityTag,
                importance: "",
              }
            : {
                id: s.id ?? s.question,
                question: s.question,
                priority_level: (s.priority_level ??
                  "Informational") as PriorityTag,
                importance: s.importance ?? "",
                dismissed: (s as any).dismissed ?? false,
                source: (s as any).source ?? undefined,
                fieldKey: (s as any).fieldKey ?? null,
              };

        const earlierArr = Array.isArray(savedEarlier)
          ? savedEarlier.map(toSQ)
          : [];
        const earlierSet = new Set(
          earlierArr.map((e) => (e.question || "").trim().toLowerCase()),
        );

        const filteredSuggested = Array.isArray(savedSuggested)
          ? (savedSuggested as any[]).map(toSQ).filter((q) => {
              const key = (q.question || "").trim().toLowerCase();
              return (
                !askedSet.has(key) &&
                !answeredSet.has(key) &&
                !dismissedSet.has(key) &&
                !earlierSet.has(key)
              );
            })
          : [];

        const isCompleted = sessionData.status === "completed";
        input.replaceSuggestions(isCompleted ? filteredSuggested : []);
        input.setEarlierFromPersisted(earlierArr);

        // Restore language settings
        if (sessionData.sourceLanguage)
          input.setSourceLang(sessionData.sourceLanguage);
        if (sessionData.targetLanguage) {
          input.setTargetLangUi(sessionData.targetLanguage);
          input.setTargetLangHook(sessionData.targetLanguage);
        }

        // Load case number + auto-location
        if (sessionData.caseId) {
          input
            .getCaseById(sessionData.caseId)
            .then((caseData) => {
              input.setCaseNumber(caseData.case_number || "");
              if (
                caseData.metadata &&
                (caseData.metadata as any).autoLocation
              ) {
                input.setAutoLocation((caseData.metadata as any).autoLocation);
              }
            })
            .catch((err) => {
              console.error("Failed to load case data:", err);
              input.setCaseNumber("");
            });
        } else {
          input.setCaseNumber("");
        }

        // Load saved utterances and insights in parallel and hydrate immediately from saved insights
        const uttPromise = input.getSessionUtterances(sessionId);
        const insPromise = input.getSessionInsights(sessionId);
        Promise.allSettled([uttPromise, insPromise])
          .then(([uttRes, insRes]) => {
            if (insRes.status === "fulfilled") {
              const insightsData = insRes.value;
              const meetingInsights = (insightsData.insights || [])
                .filter((i: any) => i.type === "meeting_insights")
                .sort(
                  (a: any, b: any) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime(),
                )[0];
              if (meetingInsights?.data) {
                input.loadSavedInsights(meetingInsights.data);
              }
            }

            if (uttRes.status === "fulfilled") {
              const utteranceData = uttRes.value;
              const savedUtterances = utteranceData.utterances.map(
                (u: any) => ({
                  id: u.id,
                  speaker: u.speaker,
                  text: u.text,
                  start: u.startTime,
                  end: u.endTime,
                  lang: u.language,
                  translation: u.translation,
                  confidence: u.confidence,
                }),
              );
              input.ingestBatch(
                savedUtterances,
                savedUtterances.map((u: any) => u.text).join(" "),
              );
            }
          })
          .catch((error) => {
            console.error("Failed to load saved data:", error);
          })
          .finally(() => {
            input.onHydrated();
          });
      })
      .catch((error) => {
        console.error("Failed to load session:", error);
        input.setIsReadOnlyMode(true);
        input.setSessionStatus("completed");
        input.onHydrated();
      });
  });
}
